import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "configurationModal",
    "configurationForm",
    "configureButton",
    "scopeField",
    "targetIdsField",
    "scopeInfo",
    "submitButton",
    "currentStatus",
    "progressBar",
    "progressPercentage",
    "successMessage",
    "errorMessage",
    "subsystemModal",
    "confirmSubsystemButton",
    "departmentModal",
    "confirmDepartmentButton",
    "signatureSection",
    "signerName",
    "signerFunction",
  ];

  static values = {
    pollInterval: { type: Number, default: 3000 },
    currentGenerationId: Number,
    selectScopeAlert: String,
    allStaffInfo: String,
    subsystemInfoPrefix: String,
    departmentInfoPrefix: String,
    generatingText: String,
    statusValidating: String,
    statusReady: String,
    statusValidationFailed: String,
    statusProcessing: String,
    statusCompleted: String,
    statusFailed: String,
  };

  connect() {
    console.log("Generate Staff Documents controller connected");
    this.selectedScope = null;
    this.selectedTargetIds = [];
    this.selectedSubsystem = null;
    this.selectedDepartmentId = null;
    this.selectedDepartmentName = null;

    // Démarrer le polling si une génération est en cours
    if (this.currentGenerationIdValue && this.hasCurrentStatusTarget) {
      this.startPolling();
    }
  }

  disconnect() {
    this.stopPolling();
  }

  // ============================================================================
  // GESTION DE LA SÉLECTION DE PORTÉE
  // ============================================================================

  selectScope(event) {
    const scopeCard = event.currentTarget;
    const scope = scopeCard.dataset.scope;
    const radioInput = scopeCard.querySelector('input[type="radio"]');

    // Réinitialiser toutes les cartes de portée (composant DS `.ds-option-card` :
    // l'état sélectionné = classe `.is-selected`, bordure verte de la charte).
    this.element.querySelectorAll("[data-scope]").forEach((card) => {
      const input = card.querySelector('input[name="generation_scope"]');
      if (input) input.checked = false;
      card.classList.remove("is-selected");
    });

    // Sélectionner le choix actuel
    if (radioInput) radioInput.checked = true;
    scopeCard.classList.add("is-selected");

    this.selectedScope = scope;
    this.updateScopeSelection(scope);

    // Activer le bouton de configuration (attribut + classe DS `is-disabled`)
    if (this.hasConfigureButtonTarget) {
      this.configureButtonTarget.disabled = false;
      this.configureButtonTarget.classList.remove("is-disabled");
    }
  }

  updateScopeSelection(scope) {
    // Mettre à jour les informations dans le modal selon la sélection
    switch (scope) {
      case "all_staff":
        this.selectedTargetIds = [];
        break;
      case "subsystem":
        this.promptSubsystemSelection();
        break;
      case "department":
        this.promptDepartmentSelection();
        break;
    }
  }

  promptSubsystemSelection() {
    // Ouvrir le modal de sélection de sous-système
    if (this.hasSubsystemModalTarget) {
      this.subsystemModalTarget.classList.remove("hidden");
      document.body.style.overflow = "hidden";
    }
  }

  promptDepartmentSelection() {
    // Ouvrir le modal de sélection de département
    if (this.hasDepartmentModalTarget) {
      this.departmentModalTarget.classList.remove("hidden");
      document.body.style.overflow = "hidden";
    }
  }

  // ============================================================================
  // GESTION DU MODAL DE CONFIGURATION
  // ============================================================================

  openConfiguration() {
    if (!this.selectedScope) {
      alert(
        this.selectScopeAlertValue ||
          "Veuillez d'abord sélectionner une portée de génération",
      );
      return;
    }

    // Mettre à jour les champs cachés
    if (this.hasScopeFieldTarget) {
      this.scopeFieldTarget.value = this.selectedScope;
    }

    if (this.hasTargetIdsFieldTarget) {
      this.targetIdsFieldTarget.value = JSON.stringify(this.selectedTargetIds);
    }

    // Mettre à jour les informations affichées
    this.updateScopeInfo();

    // Afficher le modal
    if (this.hasConfigurationModalTarget) {
      this.configurationModalTarget.classList.remove("hidden");
      document.body.style.overflow = "hidden";
    }
  }

  closeConfiguration() {
    if (this.hasConfigurationModalTarget) {
      this.configurationModalTarget.classList.add("hidden");
      document.body.style.overflow = "auto";
    }
  }

  updateScopeInfo() {
    if (!this.hasScopeInfoTarget) return;

    let infoText = "";
    switch (this.selectedScope) {
      case "all_staff":
        infoText =
          this.allStaffInfoValue ||
          "Génération pour tout le personnel de l'établissement";
        break;
      case "subsystem":
        infoText = `${this.subsystemInfoPrefixValue || "Génération pour le sous-système"} ${this.selectedTargetIds[0]}`;
        break;
      case "department":
        infoText = `${this.departmentInfoPrefixValue || "Génération pour le département"} ${
          this.selectedDepartmentName || this.selectedTargetIds[0]
        }`;
        break;
    }

    this.scopeInfoTarget.innerHTML = infoText;
  }

  // ============================================================================
  // SOUMISSION DU FORMULAIRE
  // ============================================================================

  submitConfiguration(event) {
    event.preventDefault();

    // Masquer les erreurs précédentes
    const errorContainer = document.getElementById("modal-error-container");
    if (errorContainer) {
      errorContainer.classList.add("hidden");
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.innerHTML = `<svg class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>${this.generatingTextValue || "Génération en cours..."}`;
    }

    // Soumettre le formulaire
    this.configurationFormTarget.requestSubmit();
  }

  // ============================================================================
  // POLLING POUR MISE À JOUR EN TEMPS RÉEL
  // ============================================================================

  startPolling() {
    this.stopPolling(); // S'assurer qu'il n'y a pas déjà un poll en cours

    this.pollTimer = setInterval(() => {
      this.pollGenerationStatus();
    }, this.pollIntervalValue);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async pollGenerationStatus() {
    if (!this.currentGenerationIdValue) return;

    try {
      const response = await fetch(
        `/schools/docs/generate_staff_documents/${this.currentGenerationIdValue}/status`,
        {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        this.updateGenerationStatus(data);

        // Arrêter le polling si terminé ou validation échouée
        if (
          data.status === "completed" ||
          data.status === "failed" ||
          data.status === "cancelled" ||
          data.status === "validation_failed"
        ) {
          this.stopPolling();
        }
      }
    } catch (error) {
      console.error("Erreur lors du polling:", error);
    }
  }

  updateGenerationStatus(data) {
    // Mettre à jour la barre de progression
    if (this.hasProgressBarTarget && this.hasProgressPercentageTarget) {
      let progressPercent = 0;
      let progressText = "";

      switch (data.status) {
        case "validating":
          progressPercent = 25;
          progressText = this.statusValidatingValue || "Validation...";
          break;
        case "validated":
          progressPercent = 50;
          progressText = this.statusReadyValue || "Prêt pour génération";
          break;
        case "validation_failed":
          progressPercent = 25;
          progressText =
            this.statusValidationFailedValue || "Validation échouée";
          break;
        case "processing":
          progressPercent = 75;
          progressText = this.statusProcessingValue || "Génération...";
          break;
        case "completed":
          progressPercent = 100;
          progressText = this.statusCompletedValue || "Terminé";
          break;
        case "failed":
          progressPercent = 0;
          progressText = this.statusFailedValue || "Échec";
          break;
      }

      this.progressBarTarget.style.width = `${progressPercent}%`;
      this.progressPercentageTarget.textContent = progressText;
    }

    // Recharger la page si terminé pour afficher les résultats complets
    if (data.status === "completed") {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  // ============================================================================
  // GESTION DES MESSAGES
  // ============================================================================

  dismissMessage(event) {
    const messageElement = event.currentTarget.closest(
      '[data-generate-staff-documents-target*="Message"]',
    );
    if (messageElement) {
      messageElement.style.display = "none";
    }
  }

  // ============================================================================
  // GESTION CLAVIER ET ÉVÉNEMENTS
  // ============================================================================

  handleKeydown(event) {
    // Fermer le modal avec Escape
    if (event.key === "Escape") {
      if (
        this.hasConfigurationModalTarget &&
        !this.configurationModalTarget.classList.contains("hidden")
      ) {
        this.closeConfiguration();
      } else if (
        this.hasSubsystemModalTarget &&
        !this.subsystemModalTarget.classList.contains("hidden")
      ) {
        this.closeSubsystemModal();
      } else if (
        this.hasDepartmentModalTarget &&
        !this.departmentModalTarget.classList.contains("hidden")
      ) {
        this.closeDepartmentModal();
      }
    }
  }

  // Fermer le modal en cliquant à l'extérieur
  clickOutside(event) {
    if (event.target === this.configurationModalTarget) {
      this.closeConfiguration();
    }
  }

  // ============================================================================
  // GESTION DU MODAL DE SÉLECTION DE SOUS-SYSTÈME
  // ============================================================================

  selectSubsystem(event) {
    const label = event.currentTarget;
    const subsystem = label.dataset.subsystem;
    const radioInput = label.querySelector('input[type="radio"]');

    // Désélectionner tous les autres
    this.subsystemModalTarget
      .querySelectorAll('input[name="subsystem_choice"]')
      .forEach((input) => {
        input.checked = false;
        input.closest("label").classList.remove("border-cta", "bg-cta-shared");
        input.closest("label").classList.add("border-gray-200");
      });

    // Sélectionner le choix actuel
    radioInput.checked = true;
    label.classList.remove("border-gray-200");
    label.classList.add("border-cta", "bg-cta-shared");

    this.selectedSubsystem = subsystem;

    // Activer le bouton de confirmation
    if (this.hasConfirmSubsystemButtonTarget) {
      this.confirmSubsystemButtonTarget.disabled = false;
    }
  }

  confirmSubsystemSelection() {
    if (this.selectedSubsystem) {
      this.selectedTargetIds = [this.selectedSubsystem];
      this.closeSubsystemModal();

      // Activer le bouton de configuration
      if (this.hasConfigureButtonTarget) {
        this.configureButtonTarget.disabled = false;
      }
    }
  }

  closeSubsystemModal() {
    if (this.hasSubsystemModalTarget) {
      this.subsystemModalTarget.classList.add("hidden");
      document.body.style.overflow = "auto";
    }
  }

  clickOutsideSubsystem(event) {
    if (event.target === this.subsystemModalTarget) {
      this.closeSubsystemModal();
    }
  }

  // ============================================================================
  // GESTION DU MODAL DE SÉLECTION DE DÉPARTEMENT
  // ============================================================================

  selectDepartment(event) {
    const label = event.currentTarget;
    const departmentId = label.dataset.departmentId;
    const departmentName = label.dataset.departmentName;
    const radioInput = label.querySelector('input[type="radio"]');

    // Désélectionner tous les autres
    this.departmentModalTarget
      .querySelectorAll('input[name="department_choice"]')
      .forEach((input) => {
        input.checked = false;
        input.closest("label").classList.remove("border-cta", "bg-cta-shared");
        input.closest("label").classList.add("border-gray-200");
      });

    // Sélectionner le choix actuel
    radioInput.checked = true;
    label.classList.remove("border-gray-200");
    label.classList.add("border-cta", "bg-cta-shared");

    this.selectedDepartmentId = departmentId;
    this.selectedDepartmentName = departmentName;

    // Activer le bouton de confirmation
    if (this.hasConfirmDepartmentButtonTarget) {
      this.confirmDepartmentButtonTarget.disabled = false;
    }
  }

  confirmDepartmentSelection() {
    if (this.selectedDepartmentId) {
      this.selectedTargetIds = [this.selectedDepartmentId];
      this.closeDepartmentModal();

      // Activer le bouton de configuration
      if (this.hasConfigureButtonTarget) {
        this.configureButtonTarget.disabled = false;
      }
    }
  }

  closeDepartmentModal() {
    if (this.hasDepartmentModalTarget) {
      this.departmentModalTarget.classList.add("hidden");
      document.body.style.overflow = "auto";
    }
  }

  clickOutsideDepartment(event) {
    if (event.target === this.departmentModalTarget) {
      this.closeDepartmentModal();
    }
  }

  // ============================================================================
  // GESTION DE LA SECTION SIGNATURE
  // ============================================================================

  toggleSignatureSection(event) {
    const isChecked = event.currentTarget.checked;

    if (this.hasSignatureSectionTarget) {
      if (isChecked) {
        this.signatureSectionTarget.classList.remove("hidden");
      } else {
        this.signatureSectionTarget.classList.add("hidden");
        // Réinitialiser les champs quand désactivé
        this.resetSignatureFields();
      }
    }
  }

  resetSignatureFields() {
    // Décocher les checkboxes de signature
    const checkboxes = this.signatureSectionTarget.querySelectorAll(
      'input[type="checkbox"]',
    );
    checkboxes.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = false;
      }
    });

    // Vider les champs de texte
    if (this.hasSignerNameTarget) {
      this.signerNameTarget.value = "";
    }
    if (this.hasSignerFunctionTarget) {
      this.signerFunctionTarget.value = "";
    }
  }

  updateSignaturePreview() {
    // Cette méthode peut être utilisée pour mettre à jour un aperçu
    // ou pour valider que au moins un élément est sélectionné
    const checkboxes = this.signatureSectionTarget.querySelectorAll(
      'input[type="checkbox"]:not([disabled])',
    );
    const anyChecked = Array.from(checkboxes).some((cb) => cb.checked);

    // Optionnel: afficher un message si aucun élément n'est sélectionné
    console.log("Signature elements selected:", anyChecked);
  }
}
